/**
 * CategoryContext — the entry screen's right-hand context column.
 *
 * Two cards from the v2 design: "Të fundit për këtë kategori" (12-month trend)
 * and "Periudhat që mungojnë" (per-site gaps).
 *
 * Both are derived client-side from `GET /api/invoices`, which already returns
 * `period`, `building_id`, `emissions_tco2e` and `record_type` for every
 * emission record. No new endpoint, and — more importantly — no second source
 * of truth: the numbers here are the same rows the Records screen shows.
 *
 * The prototype's third card, "Factor in use", is deliberately NOT ported. The
 * factor is not shown on entry screens (user decision, 2026-07-29); what the
 * user needs here is what they are recording, which steps 1 and 2 already say.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X } from './Icons';
import { Button, Card, Delta, Empty, Skeleton, fmt } from './ui';
import { MonthlyBars } from './ui/Charts';
import { useT } from '../../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const MONTH_ABBR = {
  sq: ['Jan', 'Shk', 'Mar', 'Pri', 'Maj', 'Qer', 'Kor', 'Gsh', 'Sht', 'Tet', 'Nën', 'Dhj'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
};

/** The last 12 calendar months ending with the current one, oldest first. */
const lastTwelve = (lang) => {
  const now = new Date();
  const abbr = MONTH_ABBR[lang] || MONTH_ABBR.sq;
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: abbr[d.getMonth()]
    };
  });
};

export function CategoryContext({ recordTypes = [], categoryLabel, refreshKey = 0, facilities = [], onPickSite }) {
  const { t, lang } = useT();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const typeKey = JSON.stringify(recordTypes);

  useEffect(() => {
    let cancelled = false;
    const types = JSON.parse(typeKey);

    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/api/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setRows((Array.isArray(data) ? data : []).filter(
          (r) => r.type === 'emission_record' && types.includes(r.record_type)
        ));
        setFailed(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load category context:', err);
        setRows([]);
        setFailed(true);
      }
    })();

    return () => { cancelled = true; };
  }, [typeKey, refreshKey]);

  const months = useMemo(() => lastTwelve(lang), [lang]);

  const { series, total, delta, deltaTitle } = useMemo(() => {
    const byPeriod = new Map(months.map((m) => [m.key, 0]));
    (rows || []).forEach((r) => {
      if (byPeriod.has(r.period)) {
        byPeriod.set(r.period, byPeriod.get(r.period) + (Number(r.emissions_tco2e) || 0));
      }
    });

    const s = months.map((m) => ({ month: m.label, current: byPeriod.get(m.key), key: m.key }));
    const sum = s.reduce((a, b) => a + b.current, 0);

    // Compare the two most recent months that actually hold data. Comparing the
    // calendar month instead would show a collapse every time someone opens this
    // screen mid-month, when the current month is by definition still partial.
    const filled = s.filter((x) => x.current > 0);
    let d = null;
    let title = null;
    if (filled.length >= 2) {
      const a = filled[filled.length - 2];
      const b = filled[filled.length - 1];
      if (a.current > 0) {
        d = ((b.current - a.current) / a.current) * 100;
        title = `${b.key} kundrejt ${a.key}`;
      }
    }

    return { series: s, total: sum, delta: d, deltaTitle: title };
  }, [rows, months]);

  const gaps = useMemo(() => {
    if (!rows) return [];
    const seen = new Set(rows.map((r) => `${r.building_id}:${r.period}`));
    return facilities
      .map((f) => ({
        id: f.id,
        name: f.name,
        missing: months.filter((m) => !seen.has(`${f.id}:${m.key}`)).length
      }))
      .filter((f) => f.missing > 0)
      .sort((a, b) => b.missing - a.missing);
  }, [rows, facilities, months]);

  const loading = rows === null;

  return (
    <>
      <Card title={t('ctx.latest')} sub={t('ctx.latestSub')}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Skeleton h={30} w="55%" />
            <Skeleton h={110} />
          </div>
        ) : failed ? (
          <Empty icon={X.Alert} title={t('ctx.loadFail')} body={t('ctx.loadFailBody')} />
        ) : total === 0 ? (
          <Empty
            icon={X.Chart}
            title={t('ctx.noHistory')}
            body={t('ctx.noHistoryBody', { category: (categoryLabel || '').toLowerCase() })}
          />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
              <span className="k2-num" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.026em' }}>
                {fmt(total, 3)}
              </span>
              <span style={{ fontSize: 12, color: 'var(--n500)' }}>{t('common.tco2e')}</span>
              {delta !== null && (
                <span style={{ marginLeft: 'auto' }} title={deltaTitle}>
                  <Delta v={delta} />
                </span>
              )}
            </div>
            <MonthlyBars data={series} height={130} showTarget={false} barColor="var(--brand-700)" />
          </>
        )}
      </Card>

      <Card title={t('ctx.missing')} sub={t('ctx.missingSub')}>
        {loading ? (
          <Skeleton h={60} />
        ) : facilities.length === 0 ? (
          <Empty icon={X.Building} title={t('ctx.noFacilities')} body={t('ctx.noFacilitiesBody')} />
        ) : gaps.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--n500)', padding: '4px 0' }}>
            {t('ctx.noGaps')}
          </div>
        ) : (
          gaps.map((g) => (
            <div className="k2-miss" key={g.id}>
              <span className="k2-ic"><X.Warn s={12} /></span>
              <span className="k2-tx">
                <span className="k2-a">{g.name}</span>
                <span className="k2-s">{t('ctx.monthsNoData', { count: g.missing })}</span>
              </span>
              {onPickSite && (
                <Button size="sm" variant="gh" onClick={() => onPickSite(g.id)}>
                  {t('ctx.add')}<X.Chev s={11} />
                </Button>
              )}
            </div>
          ))
        )}
      </Card>
    </>
  );
}

export default CategoryContext;
